import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import type { ActivityType, Day, ScheduleWeek } from "api/services/treatment/schemas/ScheduleWeek";
import { useProfileContext } from "contexts/ProfileContext";
import type {
  ProtocolCard,
  SortableProtocolCard,
} from "routes/patients/PatientProfile/components/Protocol/ProtocolTimeline/types";
import { handleType } from "routes/patients/utils/activityHelpers";
import { useLocalizedDate } from "utils/date/useLocalizedDate";
import { useCurrentPatient } from "utils/hooks/useCurrentPatient";

type ProtocolDay = {
  date: string;
  activities: ProtocolCard[];
};

type GroupedActivities = ProtocolDay[];

export const useGroupScheduleWeek = () => {
  const { t } = useTranslation();
  const { patient } = useCurrentPatient();
  const { profile } = useProfileContext();
  const { format, parseISO } = useLocalizedDate();

  invariant(profile);

  const convertToActivities = (day: Omit<Day, "date">): ProtocolCard[] => {
    const mappedActivities = day.activities.map(activity => ({
      ...activity,
      title:
        activity.activity_type === "meet_your_pt"
          ? t("activities.meet_your_pt", {
              profession: profile?.therapist_profile?.profession ?? "physical_therapist",
            })
          : activity.title,
      activityID: activity.id,
      activity_type_trimmed: handleType(activity.activity_type),
      patient_id: patient?.id || null,
    }));
    const mappedExercises = day.exercises.map(exercise => ({
      ...exercise,
      activityID: exercise.activity_type?.split("_")?.[1]
        ? parseInt(exercise.activity_type.split("_")[1], 10)
        : exercise.id,
      activity_type_trimmed: "exercise" as ActivityType,
      patient_id: patient?.id || null,
    }));
    const mappedLessons = day.lessons.map(lesson => {
      return {
        ...lesson,
        activityID: lesson.text_lesson_id,
        activity_type_trimmed: "lesson" as ActivityType,
        patient_id: patient?.id || null,
      };
    });

    return [...mappedActivities, ...mappedExercises, ...mappedLessons];
  };

  const convertProtocolCardToSortable = ({
    activity,
    day,
    week,
  }: {
    activity: ProtocolCard;
    day: number;
    week: number;
  }): SortableProtocolCard => {
    return {
      ...activity,
      day,
      sortableID: `${activity.id}-${week}-${day}`,
      week,
    };
  };

  const groupOverdueActivities = (scheduleData: ScheduleWeek | undefined): ProtocolCard[] => {
    if (!scheduleData || !scheduleData.days || Object.keys(scheduleData.days).length === 0) {
      return [];
    }

    const overdueActivities: Omit<Day, "date"> = {
      activities: [],
      exercises: [],
      lessons: [],
    };

    Object.values(scheduleData.days).forEach(day => {
      overdueActivities.activities.push(...day.activities.filter(activity => activity.overdue && activity.required));
      overdueActivities.exercises.push(...day.exercises.filter(exercise => exercise.overdue));
      overdueActivities.lessons.push(...day.lessons.filter(lesson => lesson.overdue));
    });

    return convertToActivities(overdueActivities);
  };

  const groupData = (scheduleData: ScheduleWeek | undefined): GroupedActivities => {
    if (!scheduleData || !scheduleData.days || Object.keys(scheduleData.days).length === 0) {
      return [];
    }

    return Object.values(scheduleData.days).map(day => {
      return {
        date: format(parseISO(day.date), "yyyy-MM-dd"),
        activities: convertToActivities(day),
      };
    });
  };

  return { groupOverdueActivities, convertProtocolCardToSortable, groupData };
};
